import {
  totalOf,
  type TotalAmount,
  type UnitRate,
} from './money';
import { ApiShapeError } from './task-api';

/**
 * ============================ RAPOR SAYILARI ================================
 *
 * Bu dosyanin tek isi, sunucudan gelen ham bir sayinin NE OLDUGUNU tip
 * duzeyinde isaretlemek. Uc rapor ucu da "para gibi gorunen" sayilar donuyor
 * ama BIRBIRINDEN FARKLI seyler oluyorlar ve ikisi karistirildiginda ekranda
 * hicbir hata gorunmuyor — sadece yanlis rakam.
 *
 * --------------------------- HACIM FORMULU ---------------------------------
 *
 *     HACIM (TotalAmount) = offerPrice (UnitRate)  ×  taskAmount
 *
 * `offer_price` 1 BIRIM dovizin TL fiyatidir (orn. 44,2150), islemin toplami
 * DEGIL. Bir islemin cirosu ancak miktarla carpildiginda ortaya cikar.
 * Formulun TEK tanimi asagidaki `volumeOf`; baska hicbir yerde tekrar
 * yazilmaz (bkz. src/lib/money.ts — markali tipler bunu zorunlu kiliyor).
 *
 * ------------------- HANGI UC HANGISINI DONUYOR (DOGRULANDI) ---------------
 *
 * 1) GET /v1/dashboards/exchange
 *    Sunucu hacmi KENDISI hesapliyor:
 *      db.js -> calculateVolume = ({taskAmount, offerPrice}) => taskAmount * offerPrice
 *    Yani `accepted.sum`, `rejected.sum`, `warning.sum` ve para birimi
 *    kirilimlari GERCEK HACIM. -> `serverVolume()` ile TotalAmount isaretlenir.
 *    (Kapsam notu: bu uc `task_offer.offer_user_id = caller` ile suzuluyor,
 *    yani ORGANIZASYONUN degil KULLANICININ KENDI cirosu.)
 *
 * 2) GET /v1/reports/offers-summary, /v1/reports/tasks-summary,
 *    /v1/reports/member-activity
 *    Bu uclar `COALESCE(sum(o.offer_price), 0)` donuyor — miktarla CARPIM YOK.
 *    Yani `totalOfferPrice` / `wonOfferPrice` / `totalPrice` bir HACIM DEGIL,
 *    BIRIM KURLARIN TOPLAMI. -> `rateSum()` ile UnitRate isaretlenir.
 *
 *    Bunu "hacim" diye etiketlemek gercek bir hata: 2711 teklifin kur toplami
 *    119.767 TL gibi gorunur ama o rakamin ciroyla ilgisi yoktur (ortalama kur
 *    ~44 TL x 2711 teklif). Mobil taraf bu sayiyi "Kazanılan hacim" olarak
 *    basiyor; web tarafta BILINCLI olarak oyle etiketlenmiyor ve markali tip
 *    <MoneyText total=…> icine gecirilmesini DERLEME HATASI yapiyor.
 *
 * ---------------------------------------------------------------------------
 * Dosya adi `report-` onekli: bu klasordeki paylasilan yardimcilar icin
 * uzerinde anlasilmis dosya sahipligi kurali (bkz. src/lib/task-*.ts).
 * ========================================================================== */

/**
 * HACIM FORMULU — tek tanim.
 *
 *     volumeOf(unitRate(offerPrice), taskAmount) === offerPrice * taskAmount
 *
 * Sunucunun `calculateVolume`'u ile BIREBIR ayni; esdegerligi
 * src/lib/__tests__/report-volume.test.ts dogruluyor.
 */
export const volumeOf = (rate: UnitRate, taskAmount: number): TotalAmount =>
  totalOf(rate, taskAmount);

/**
 * HAM SAYI OKUMA — bozuk deger SESSIZCE 0 OLMAZ.
 *
 * pg `numeric` alanlarini STRING dondurur ("11228.91"), `count(*)::int` ise
 * gercek bir sayi. Ikisi de kabul edilir. Ama `null`, `undefined`, nesne ya da
 * sayiya cevrilemeyen bir dize ATILIR: `Number(x) || 0` yazsaydik alan adi
 * degistigi gun ekran "0,00 TL" gosterir ve bunu ne tsc ne test yakalardi —
 * "veri yok" ile "yaniti anlayamadim" ayni ekrani gostermemeli.
 */
export const parseNumeric = (value: unknown, field: string): number => {
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new ApiShapeError(`${field} sonlu bir sayi degil`);
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  throw new ApiShapeError(`${field} sayi degil (${typeof value})`);
};

/**
 * Tam sayi sayaclari (offerCount, wonCount, taskCount).
 *
 * Bunlar `count(*)::int` sonucu; KIMLIK DEGIL, bu yuzden sayiya cevrilmeleri
 * guvenli (kimlikler string kalir — bkz. src/auth/types.ts).
 */
export const parseCount = (value: unknown, field: string): number => {
  const parsed = parseNumeric(value, field);
  if (!Number.isInteger(parsed)) throw new ApiShapeError(`${field} tam sayi degil`);
  return parsed;
};

/**
 * SUNUCUNUN HESAPLADIGI HACIM -> TotalAmount.
 *
 * Yalniz `/v1/dashboards/exchange` icin gecerli: o uc `taskAmount *
 * offerPrice` carpimini kendisi yapiyor. Ham bir `offer_price` toplamini bu
 * fonksiyondan gecirmek, formulu atlamak demektir — `rateSum()` kullanin.
 */
export const serverVolume = (value: unknown, field: string): TotalAmount =>
  parseNumeric(value, field) as TotalAmount;

/**
 * `SUM(offer_price)` -> UnitRate (BIRIM KURLARIN TOPLAMI, HACIM DEGIL).
 *
 * Markali tip kasitli: bu deger <MoneyText total=…> icine gecirilemez, yani
 * "toplam tutar" olarak cizilmesi derleme hatasi olur.
 */
export const rateSum = (value: unknown, field: string): UnitRate =>
  parseNumeric(value, field) as UnitRate;

/** TotalAmount'lari toplar — sonuc yine TotalAmount (birim degismiyor). */
export const sumTotals = (values: readonly TotalAmount[]): TotalAmount =>
  values.reduce<number>((acc, value) => acc + value, 0) as TotalAmount;

/** UnitRate toplamlarini toplar. Yine bir hacim DEGIL. */
export const sumRates = (values: readonly UnitRate[]): UnitRate =>
  values.reduce<number>((acc, value) => acc + value, 0) as UnitRate;

/**
 * Kazanma orani — yuzde (0-100). Payda 0 ise `null`: "%0" yazmak, hic teklif
 * verilmemis bir donemi "hepsini kaybettin" gibi gosterirdi.
 */
export const winRatePercent = (won: number, total: number): number | null =>
  total > 0 ? Math.round((won / total) * 100) : null;
